import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker } from "react-day-picker";

import { cn } from "../lib/utils";
import { buttonVariants } from "./button";

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

function Calendar({
  className,
  classNames,
  showOutsideDays = false,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      // Without this, DayPicker renders one shared Nav bar above the caption
      // instead of flanking it, so `month`'s `relative` below isn't even an
      // ancestor of the prev/next buttons and their `absolute` positioning
      // resolves against something far up the tree instead — which is why
      // the arrows were drifting down into the day grid.
      navLayout="around"
      className={cn("p-3", className)}
      classNames={{
        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
        // `relative` here is the containing block the prev/next buttons
        // below position themselves against — they're siblings of the
        // caption inside `month`, not descendants of it.
        month: "space-y-4 relative",
        // react-day-picker 9 renamed `caption` -> `month_caption` and split
        // `nav_button`/`nav_button_previous`/`nav_button_next` into
        // `button_previous`/`button_next`. The v8 names below matched
        // nothing, so none of this applied at all.
        month_caption: "flex justify-center pt-1 items-center",
        caption_label: "text-sm font-medium",
        nav: "space-x-1 flex items-center",
        button_previous: cn(
          buttonVariants({ variant: "outline" }),
          "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 absolute left-1 top-1"
        ),
        button_next: cn(
          buttonVariants({ variant: "outline" }),
          "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 absolute right-1 top-1"
        ),
        table: "w-full border-collapse space-y-1",
        // react-day-picker 9 renamed these too (`head_row` -> `weekdays`,
        // `head_cell` -> `weekday`); the v8 names below matched nothing, so
        // the weekday header row never got its flex layout or per-column
        // width and every label collapsed onto one line in source order.
        weekdays: "flex",
        weekday:
          "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]",
        row: "flex w-full mt-2",
        cell: "h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
        // This used to be `buttonVariants({ variant: "ghost" })`, whose hover
        // applied to every cell alike — a greyed-out past day lit up as if it
        // could be picked, and the selected day lost its fill under the
        // pointer. Same look, but the hover now skips selected and disabled
        // cells (DayPicker marks them with data-selected / data-disabled).
        day: cn(
          "inline-flex items-center justify-center whitespace-nowrap rounded-sm text-sm font-normal text-text-200 transition-colors",
          "h-9 w-9 p-0 aria-selected:opacity-100",
          "[&:not([data-selected]):not([data-disabled])]:hover:bg-secondary-100 [&:not([data-selected]):not([data-disabled])]:hover:text-primary",
          "dark:[&:not([data-selected]):not([data-disabled])]:hover:bg-card dark:[&:not([data-selected]):not([data-disabled])]:hover:text-foreground"
        ),
        // In v9 `day` is the <td> and the clickable <button> inside it is
        // `day_button`. With no class the button shrank to its text, so the
        // whole cell lit up on hover but only a click on the digits landed.
        // Stretch it over the cell so the hover area and hit area agree.
        day_button:
          "h-full w-full inline-flex items-center justify-center rounded-[inherit] cursor-pointer disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-secondary",
        day_selected:
          "bg-primary text-white-both-modes hover:bg-primary hover:text-white-both-modes focus:bg-primary focus:text-white-both-modes",
        day_today: "!bg-transparent !border !border-primary !text-primary",
        outside: "text-muted-foreground opacity-50",
        // react-day-picker 9 renamed every classNames key (`day_disabled` ->
        // `disabled`), so the v8 names around this line no longer match
        // anything. Sweeping them all up is a change to how every calendar in
        // the app looks; this one key is not optional, because a day that
        // cannot be clicked has to look like it — otherwise the calendar
        // simply swallows the click and says nothing.
        disabled: "text-muted-foreground opacity-40",
        day_range_start:
          "rounded-l-md bg-primary text-primary-foreground font-semibold",
        day_range_end:
          "rounded-r-md bg-primary text-primary-foreground font-semibold",
        day_range_middle:
          "aria-selected:bg-secondary/50 rounded-none aria-selected:text-foreground hover:bg-secondary/60",
        // Governs the placeholder cells left by `showOutsideDays={false}` —
        // also stale as `day_hidden` in v8.
        hidden: "invisible",
        ...classNames,
      }}
      components={{
        // v9 replaced the separate IconLeft/IconRight overrides with a
        // single Chevron component keyed off `orientation`.
        Chevron: ({ orientation, className }) =>
          orientation === "right" ? (
            <ChevronRight className={cn("h-4 w-4", className)} />
          ) : (
            <ChevronLeft className={cn("h-4 w-4", className)} />
          ),
      }}
      modifiersClassNames={{
        selected: "bg-primary text-white-both-modes",
        today: "!bg-bg-500 !border !border-primary !text-primary",
        range_start: "bg-transparent border border-primary text-primary font-bold",
        range_end: "bg-transparent border border-primary !text-primary font-bold",
        range_middle:
          "bg-secondary hover:bg-secondary hover:text-white-both-modes !rounded-none text-secondary-foreground",
        ...(props.modifiersClassNames || {}),
      }}
      {...props}
    />
  );
}

Calendar.displayName = "Calendar";

export { Calendar };
